import { AppRoutes } from 'config/appRoutes';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

interface Args {
  pathname?: AppRoutes;

  stepParamName?: string;
  initialStep?: number;
  maxStep?: number;
  minStep?: number;
}

/** Used for pages that use a param to determine their current step progress
 * Does several things:
 *  - initializes step if it wasn't included in the url
 *  - reverts to first step if step is less than minStep
 *  - reverts to first step if step is greater than maxStep
 */
export const useInitializeStep = ({
  pathname,
  stepParamName = 'step',
  initialStep = 1,
  maxStep,
  minStep = 1,
}: Args) => {
  const router = useRouter();

  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    if (!(stepParamName in router.query)) {
      router.replace(
        {
          pathname: pathname ?? router.pathname,
          query: { ...router.query, [stepParamName]: initialStep },
        },
        undefined,
        { shallow: true, scroll: true },
      );
      return;
    }

    if (stepParamName in router.query) {
      const step = router.query[stepParamName];
      if (maxStep) {
        if (Number(step) > maxStep) {
          router.replace(
            {
              pathname: pathname ?? router.pathname,
              query: { ...router.query, [stepParamName]: initialStep },
            },
            undefined,
            { shallow: true, scroll: true },
          );
          return;
        }
        if (minStep) {
          if (Number(step) < minStep) {
            router.replace(
              {
                pathname: pathname ?? router.pathname,
                query: { ...router.query, [stepParamName]: initialStep },
              },
              undefined,
              { shallow: true, scroll: true },
            );
            return;
          }
        }
      }
    }
  }, [initialStep, maxStep, minStep, pathname, router, stepParamName]);
};
